// lib/services/eventos.ts
import { connection } from '@/lib/connection'
import { apiUrl } from '@/lib/config'

import { EntradaSeleccionada } from '@/schemas/checkout.schema';
import { Evento, EventoPublicado } from '@/types';

// Usar la función apiUrl que detecta automáticamente si estamos en servidor o cliente
const API_URL = apiUrl('/eventos'); // LEGACY EVITAR A TODA COSTA

/* Helpers */
const ts = () => `_ts=${Date.now()}`;

/* ───────────────────────────── Listado ───────────────────────────── */
export async function getEventos(cookies?: string): Promise<Evento[]> {
  const url = `${API_URL}?${ts()}`;
  const data = await connection<any>(url, 'GET', {}, null, cookies);
  if (Array.isArray(data)) return data as Evento[];
  return Array.isArray(data?.eventos) ? data.eventos : [];
}

/* Destacados */
export async function getEventosDestacados(cookies?: string): Promise<Evento[]> {
  const url = `${API_URL}/eventosDestacados?${ts()}`;
  const data = await connection<any>(url, 'GET', {}, null, cookies);
  if (Array.isArray(data)) return data as Evento[];
  return Array.isArray(data?.eventos) ? data.eventos : (data ?? []);
}

/* Marcar destacado */
export const destacarEvento = async (id: string, destacado: boolean) => {
  await connection(`${API_URL}/destacado/${id}`, 'PUT', {}, { destacado });
};

/* Publicados (listado) */
export async function getEventosPublicados(cookies?: string): Promise<EventoPublicado[]> {
  const url = `${API_URL}/publicados?${ts()}`;
  const data = await connection<any>(url, 'GET', {}, null, cookies);
  if (Array.isArray(data)) return data as EventoPublicado[];
  return Array.isArray(data?.eventos) ? data.eventos : [];
}

/* ─────── Obtener un evento por id (robusto a distintos shapes) ─────── */
export async function getEventoById(id: string, cache: boolean = true, cookies?: string): Promise<any | null> {
  const url = `${API_URL}/${id}?${ts()}`;
  console.log('[getEventoById] GET', url);

  const data = await connection<any>(url, 'GET', {}, null, cookies, cache);

  // Respuestas típicas
  if (data?.evento && typeof data.evento === 'object') return data.evento;
  if (data?.event && typeof data.event === 'object') return data.event;
  if (data && !data?.eventos && typeof data === 'object') return data;

  // Fallback si vino el listado { eventos: [...] }
  if (Array.isArray(data?.eventos)) {
    const found = data.eventos.find((e: any) => Number(e?.id) === Number(id));
    return found ?? null;
  }

  return null;
}

/* ─────── Actualizar evento con FormData (texto + imágenes) ─────── */
export async function actualizarEvento(id: string, formData: FormData, cookies?: string): Promise<{ id: string }> {
  const res = await fetch(`${API_URL}/${id}`, {
    method: 'PUT',
    body: formData,
    headers: cookies ? { cookie: cookies } : undefined,
  });
  if (!res.ok) {
    const error = await res.text();
    throw new Error(`Error al actualizar evento: ${res.status} - ${error}`);
  }
  return await res.json();
}

/* Clonar */
export async function clonarEvento(evento: Evento): Promise<void> {
  const nuevoEvento = {
    nombre: `${evento.nombre} (Copia)`,
    fecha: (evento as any).fecha, // mantener mismo shape que usás actualmente
    artista: evento.artista
  };
  await connection(API_URL, 'POST', {}, nuevoEvento);
}

/* Modificar por JSON (solo texto) */
export async function modificarEvento(evento: Evento): Promise<void> {
  await connection(`${API_URL}/${evento.id}`, 'PUT', {}, {
    nombre: evento.nombre,
    artista: evento.artista,
  });
}

/* Crear */
export async function nuevoEvento(formData: FormData): Promise<{ id: string }> {
  const res = await fetch(API_URL, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const error = await res.text();
    throw new Error(`Error al crear evento: ${res.status} - ${error}`);
  }

  return await res.json();
}

/* Borrar */
export async function borrarEvento(id: string): Promise<void> {
  await connection(`${API_URL}/${id}`, 'DELETE');
}

/* Reservar entradas */
export async function reserveEntradas(funcionId: string, entradas: EntradaSeleccionada[]): Promise<{ reservaId: string }> {
  return await connection<{ reservaId: string }>(`${API_URL}/reserve`, 'POST', {}, { funcionId, entradas });
}

export async function releaseEntradas(funcionId: string, reservaId: string): Promise<boolean> {
  const data = await connection<{ success: boolean }>(`${API_URL}/release`, 'POST', {}, { funcionId, reservaId });
  return data.success;
}

export function trackCompraSSE(compraId: string) {
  const url = `${apiUrl('/compras')}/${compraId}/track`;
  return new EventSource(url);
}